// Aufbau-Editor: Bausteine stehen fest, Busse werden von Anschluss zu Anschluss gezogen
// (oder zwei Anschlüsse nacheinander angetippt). Ein weiterer Anschluss an einem bestehenden Bus
// wird zur Abzweigung. Bus antippen → Breite mit −/+ einstellen oder löschen.

import { reducedMotion } from "./confetti.ts";
import { ICON } from "./icons.ts";
import { busGeo, layout, VIEW, W } from "./layout.ts";
import type { Layout } from "./layout.ts";
import { ARTEN, checkAufbau } from "./model.ts";
import type { AufbauLoesung, AufbauResult, AufbauTask, Bus } from "./model.ts";
import { store } from "./store.ts";

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
const MAX_BREITE = 64; // wie im Prüfer (heutiger PC: 64 Datenleitungen)

interface Drag { from: string; x0: number; y0: number; x: number; y: number; moved: boolean }

export class AufbauView {
  private busse: Bus[];
  private selected: number | null = null; // Index des ausgewählten Busses
  private pending: string | null = null;  // angetippter Anschluss, wartet auf den zweiten
  private drag: Drag | null = null;
  private undoStack: string[] = [];
  private result: AufbauResult | null = null;
  private hintsShown: number;
  private fails: number;
  private popStars = false;
  private flowTimer: number | null = null;

  private readonly lay: Layout;
  private readonly art: Map<string, string>; // Anschluss → Bausteinart
  private readonly svg: SVGSVGElement;
  private readonly side: HTMLElement;
  private readonly root: HTMLElement;
  private readonly task: AufbauTask;
  private readonly next: { id: string; titel: string } | null;

  constructor(root: HTMLElement, task: AufbauTask, next: { id: string; titel: string } | null = null) {
    this.root = root;
    this.task = task;
    this.next = next;
    this.lay = layout(task);
    this.art = new Map(task.bausteine.flatMap(b => b.ports.map(p => [`${b.id}.${p.id}`, b.art] as [string, string])));
    const saved = store.busse(task.id);
    this.busse = (saved?.busse ?? []).filter(b => b.ports.every(r => r in this.lay.ports));
    this.hintsShown = store.hints(task.id);
    this.fails = store.fails(task.id);

    root.innerHTML = `
      <section class="task-head">
        <a class="back" href="#">← Aufgaben</a>
        <h1>${esc(task.titel)}</h1>
        <p class="barometer" hidden></p>
        ${task.text ? `<p>${esc(task.text)}</p>` : ""}
      </section>
      <div class="workbench zyklus">
        <div class="canvas-wrap">
          <svg class="canvas aufbau" viewBox="0 ${VIEW.y} ${W} ${VIEW.h}" role="application"
               aria-label="Bausteine und Busse"><g class="layer"></g></svg>
          <div class="toolbar">
            <button data-act="undo" title="Rückgängig">${ICON.undo}<span>Rückgängig</span></button>
            <button data-act="delete" title="Ausgewählten Bus löschen">${ICON.trash}<span>Löschen</span></button>
            <button data-act="clear" title="Alle Busse entfernen">Neu</button>
            <span class="breite" hidden>
              <span class="breite-label"></span>
              <button data-act="minus" aria-label="Eine Leitung weniger">−</button>
              <input type="number" inputmode="numeric" min="1" max="${MAX_BREITE}" aria-label="Anzahl Leitungen">
              <span class="breite-unit">Leitungen</span>
              <button data-act="plus" aria-label="Eine Leitung mehr">+</button>
            </span>
            <span class="spacer"></span>
            <button data-act="hint" class="secondary"></button>
            <button data-act="check" class="primary">Prüfen</button>
          </div>
        </div>
        <aside class="side"></aside>
      </div>`;
    this.svg = root.querySelector("svg.canvas")!;
    this.side = root.querySelector(".side")!;

    this.svg.addEventListener("pointerdown", e => this.onDown(e));
    this.svg.addEventListener("pointermove", e => this.onMove(e));
    this.svg.addEventListener("pointerup", e => this.onUp(e));
    this.svg.addEventListener("pointercancel", () => { this.drag = null; this.render(); });
    // Scrollen per Touch nur verhindern, wenn pointerdown (kommt vor touchstart) ein Ziehen begonnen hat
    this.svg.addEventListener("touchstart", e => { if (this.drag) e.preventDefault(); }, { passive: false });
    root.querySelector<HTMLInputElement>(".breite input")!.addEventListener("change", e => {
      const n = Number((e.target as HTMLInputElement).value);
      const bus = this.selected === null ? null : this.busse[this.selected];
      if (bus && Number.isInteger(n) && n >= 1 && n <= MAX_BREITE && n !== bus.breite) this.mutate(() => { bus.breite = n; });
      else this.render();
    });
    root.querySelector(".toolbar")!.addEventListener("click", e => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act) this.action(act);
    });
    this.render();
  }

  destroy(): void {
    if (this.flowTimer !== null) window.clearTimeout(this.flowTimer);
  }

  // ---------------------------------------------------------------- Bearbeiten

  private mutate(fn: () => void): void {
    this.undoStack.push(JSON.stringify(this.busse));
    if (this.undoStack.length > 100) this.undoStack.shift();
    fn();
    this.busse = this.busse.filter(b => b.ports.length >= 2);
    store.saveBusse(this.task.id, { busse: this.busse });
    this.result = null;
    this.stopFlow();
    this.render();
  }

  private busOf(ref: string): number {
    return this.busse.findIndex(b => b.ports.includes(ref));
  }

  /** Verbindet zwei Anschlüsse: neuer Bus, Abzweig an bestehenden Bus oder zwei Busse verschmelzen. */
  private connect(a: string, b: string): void {
    const ia = this.busOf(a), ib = this.busOf(b);
    if (a === b || (ia >= 0 && ia === ib)) {
      this.render();
      return;
    }
    this.mutate(() => {
      if (ia < 0 && ib < 0) {
        const art = this.lay.ports[a].art;
        this.busse.push({ ports: [a, b], breite: art === "steuer" ? null : 1 });
        this.selected = this.busse.length - 1;
      } else if (ia >= 0 && ib >= 0) {
        this.busse[ia].ports.push(...this.busse[ib].ports);
        this.busse.splice(ib, 1);
        this.selected = ia > ib ? ia - 1 : ia;
      } else {
        const i = ia >= 0 ? ia : ib;
        this.busse[i].ports.push(ia >= 0 ? b : a);
        this.selected = i;
      }
    });
  }

  private action(act: string): void {
    switch (act) {
      case "undo": {
        const prev = this.undoStack.pop();
        if (prev === undefined) return;
        this.busse = JSON.parse(prev);
        this.selected = null;
        store.saveBusse(this.task.id, { busse: this.busse });
        this.result = null;
        this.render();
        return;
      }
      case "delete": {
        const i = this.selected;
        if (i === null) return;
        this.selected = null;
        this.mutate(() => { this.busse.splice(i, 1); });
        return;
      }
      case "clear":
        if (!this.busse.length) return;
        this.selected = null;
        this.mutate(() => { this.busse = []; });
        return;
      case "minus":
      case "plus": {
        const bus = this.selected === null ? null : this.busse[this.selected];
        if (!bus || bus.breite === null || bus.breite === undefined) return;
        const n = bus.breite + (act === "plus" ? 1 : -1);
        if (n < 1 || n > MAX_BREITE) return;
        this.mutate(() => { bus.breite = n; });
        return;
      }
      case "hint":
        if (this.hintsShown < this.task.hinweise.length) {
          this.hintsShown++;
          store.saveHints(this.task.id, this.hintsShown);
          this.render();
        }
        return;
      case "check": return this.runCheck();
    }
  }

  // ---------------------------------------------------------------- Zeiger

  private point(e: PointerEvent): [number, number] {
    const m = this.svg.getScreenCTM();
    if (!m) return [0, 0];
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return [p.x, p.y];
  }

  private portAt(e: PointerEvent): string | null {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    return el?.closest<SVGElement>("[data-port]")?.dataset.port ?? null;
  }

  private onDown(e: PointerEvent): void {
    const target = e.target as Element;
    const port = target.closest<SVGElement>("[data-port]")?.dataset.port;
    if (port) {
      e.preventDefault();
      const [x, y] = this.point(e);
      this.drag = { from: port, x0: x, y0: y, x, y, moved: false };
      return;
    }
    const bus = target.closest<SVGElement>("[data-bus]")?.dataset.bus;
    this.pending = null;
    this.selected = bus === undefined ? null : Number(bus);
    this.render();
  }

  private onMove(e: PointerEvent): void {
    if (!this.drag) return;
    const [x, y] = this.point(e);
    this.drag.x = x;
    this.drag.y = y;
    if (Math.hypot(x - this.drag.x0, y - this.drag.y0) > 10) this.drag.moved = true;
    if (this.drag.moved) this.render();
  }

  private onUp(e: PointerEvent): void {
    const drag = this.drag;
    if (!drag) return;
    this.drag = null;
    const over = this.portAt(e);
    if (drag.moved) {
      this.pending = null;
      if (over && over !== drag.from) this.connect(drag.from, over);
      else this.render();
      return;
    }
    // Antippen: erster Anschluss merken, zweiter verbindet; nochmal derselbe hebt die Wahl auf
    if (this.pending && this.pending !== drag.from) {
      const a = this.pending;
      this.pending = null;
      this.connect(a, drag.from);
    } else {
      this.pending = this.pending === drag.from ? null : drag.from;
      const i = this.busOf(drag.from);
      this.selected = i >= 0 ? i : null;
      this.render();
    }
  }

  // ---------------------------------------------------------------- Prüfen

  private stars(): number {
    return 1 + (this.fails === 0 ? 1 : 0) + (this.fails === 0 && this.hintsShown === 0 ? 1 : 0);
  }

  private runCheck(): void {
    const loesung: AufbauLoesung = { busse: this.busse };
    this.result = checkAufbau(this.task.bausteine, loesung);
    this.selected = null;
    this.pending = null;
    this.root.dispatchEvent(new CustomEvent("checked", { detail: { loesung, hints: this.hintsShown } }));
    if (!this.result.solved) {
      this.fails++;
      store.saveFails(this.task.id, this.fails);
      this.render();
      return;
    }
    const stars = this.stars();
    const before = store.stars();
    store.saveStars(this.task.id, stars);
    this.root.dispatchEvent(new CustomEvent("solved", { detail: { stars, before } }));
    this.popStars = true;
    this.render();
    this.popStars = false;
    if (!reducedMotion()) {
      this.svg.classList.add("flowing");
      this.flowTimer = window.setTimeout(() => this.stopFlow(), 4000);
    }
  }

  private stopFlow(): void {
    if (this.flowTimer !== null) window.clearTimeout(this.flowTimer);
    this.flowTimer = null;
    this.svg.classList.remove("flowing");
  }

  // ---------------------------------------------------------------- Darstellung

  private render(): void {
    this.svg.querySelector("g.layer")!.innerHTML = this.drawing();
    this.renderToolbar();
    this.renderSide();
  }

  /** Fehlerhafte Busse und Anschlüsse aus dem letzten Prüfergebnis. */
  private errors(): { busse: Set<number>; ports: Set<string> } {
    const busse = new Set<number>(), ports = new Set<string>();
    for (const p of this.result?.problems ?? []) {
      if (!p.ref) continue;
      if (p.ref.startsWith("bus:")) busse.add(Number(p.ref.slice(4)));
      else if (p.code === "offen") ports.add(p.ref);
      else {
        const i = this.busOf(p.ref);
        if (i >= 0) busse.add(i);
        ports.add(p.ref);
      }
    }
    return { busse, ports };
  }

  private drawing(): string {
    const { boxes, ports, trennlinie } = this.lay;
    const err = this.errors();
    const parts: string[] = [];

    // Busse unter den Bausteinen
    this.busse.forEach((bus, i) => {
      const art = this.lay.ports[bus.ports[0]].art;
      const geo = busGeo(bus.ports, ports, r => this.art.get(r)!, Object.values(boxes));
      const d = geo.segs.map(([x0, y0, x1, y1]) => `M${x0} ${y0}L${x1} ${y1}`).join("");
      const c = ["bus", art, i === this.selected ? "selected" : "", err.busse.has(i) ? "error" : ""].join(" ");
      parts.push(`<g class="${c}" data-bus="${i}">
        <path class="bus-hit" d="${d}"/>
        <path class="bus-line" d="${d}"/>
        ${geo.dots.map(([x, y]) => `<circle class="bus-dot" cx="${x}" cy="${y}" r="8"/>`).join("")}
        ${bus.breite ? this.pill(geo.pill[0], geo.pill[1], bus.breite) : ""}
      </g>`);
    });

    for (const [id, b] of Object.entries(boxes)) {
      parts.push(`<g class="baustein ${id}">
        <rect class="box" x="${b.x}" y="${b.y}" width="${b.w}" height="${b.h}" rx="14"/>
        <text class="box-title" x="${b.x + b.w / 2}" y="${b.y + (b.h > 100 ? 36 : 48)}">${b.titel}</text>
        ${b.unter.map((u, k) => `<text class="box-sub" x="${b.x + b.w / 2}" y="${b.y + (b.h > 100 ? 62 : 68) + k * 22}">${esc(u)}</text>`).join("")}
      </g>`);
    }
    if (trennlinie) {
      const c = boxes.cpu;
      parts.push(`<line class="cpu-split" x1="${trennlinie}" y1="${c.y + 50}" x2="${trennlinie}" y2="${c.y + c.h - 12}"/>
        <text class="box-sub" x="${(c.x + trennlinie) / 2}" y="${c.y + c.h - 24}">Steuerwerk</text>
        <text class="box-sub" x="${(trennlinie + c.x + c.w) / 2}" y="${c.y + c.h - 24}">Rechenwerk</text>
        <text class="side-sub" x="${(c.x + trennlinie) / 2}" y="${c.y + 80}">Befehle</text>
        <text class="side-sub" x="${(trennlinie + c.x + c.w) / 2}" y="${c.y + 80}">Daten</text>`);
    }

    // Anschlüsse (über den Bausteinen, große Trefferfläche)
    for (const [ref, p] of Object.entries(ports)) {
      const lx = p.dir === "r" ? p.x - 18 : p.dir === "l" ? p.x + 18 : p.x;
      const ly = p.dir === "u" ? p.y + 20 : p.y + 5;
      const c = ["port", p.art, this.busOf(ref) >= 0 ? "used" : "", ref === this.pending || ref === this.drag?.from ? "hot" : "",
        err.ports.has(ref) ? "error" : ""].join(" ");
      parts.push(`<g class="${c}" data-port="${ref}" role="button" aria-label="${ARTEN[p.art]}-Anschluss ${ref}">
        <circle class="port-hit" cx="${p.x}" cy="${p.y}" r="24"/>
        <circle class="port-dot" cx="${p.x}" cy="${p.y}" r="9"/>
        <text class="port-label" x="${lx}" y="${ly}">${p.label}</text>
      </g>`);
    }

    if (this.drag?.moved) {
      const p = ports[this.drag.from];
      parts.push(`<line class="rubber" x1="${p.x}" y1="${p.y}" x2="${this.drag.x}" y2="${this.drag.y}"/>`);
    }
    return parts.join("");
  }

  /** Busbreite wie im Schaltplan: Schrägstrich über der Leitung, daneben die Anzahl. */
  private pill(x: number, y: number, n: number): string {
    return `<g class="pill">
      <line x1="${x - 26}" y1="${y + 9}" x2="${x - 14}" y2="${y - 9}"/>
      <rect x="${x - 10}" y="${y - 13}" width="${n >= 10 ? 34 : 26}" height="26" rx="13"/>
      <text x="${x + (n >= 10 ? 7 : 3)}" y="${y + 5}">${n}</text>
    </g>`;
  }

  private renderToolbar(): void {
    const q = (act: string) => this.root.querySelector<HTMLButtonElement>(`[data-act="${act}"]`)!;
    q("undo").disabled = this.undoStack.length === 0;
    q("delete").disabled = this.selected === null;
    q("clear").disabled = this.busse.length === 0;
    const hint = q("hint");
    const left = this.task.hinweise.length - this.hintsShown;
    hint.innerHTML = `${ICON.bulb}<span>Hinweis${left > 0 ? ` (${left})` : ""}</span>`;
    hint.disabled = left <= 0;
    hint.hidden = this.task.hinweise.length === 0;

    const box = this.root.querySelector<HTMLElement>(".breite")!;
    const bus = this.selected === null ? null : this.busse[this.selected];
    const n = bus?.breite;
    box.hidden = !bus || n === null || n === undefined;
    if (bus && n) {
      box.querySelector(".breite-label")!.textContent = `${ARTEN[this.lay.ports[bus.ports[0]].art]}:`;
      const input = box.querySelector("input")!;
      if (document.activeElement !== input) input.value = String(n);
      box.querySelector(".breite-unit")!.textContent = n === 1 ? "Leitung" : "Leitungen";
      q("minus").disabled = n <= 1;
      q("plus").disabled = n >= MAX_BREITE;
    }
  }

  private renderSide(): void {
    const t = this.task, r = this.result;
    let feedback = "";
    if (r?.solved) {
      const stars = this.stars();
      const onward = this.next
        ? `<a class="next-task" href="#${this.next.id}">Nächste Aufgabe: ${esc(this.next.titel)} ${ICON.arrow}</a>`
        : `<a class="next-task" href="#">Zur Übersicht ${ICON.arrow}</a>`;
      feedback = `<div class="feedback success">
        <div class="stars${this.popStars ? " pop" : ""}">${"★".repeat(stars)}${"☆".repeat(3 - stars)}</div>
        <strong>Richtig verkabelt!</strong>
        ${t.architektur === "harvard"
          ? "Befehle und Daten haben jeder ihren eigenen Bus -- die CPU kann beides gleichzeitig holen."
          : "Programm und Daten teilen sich einen Bus -- das ist der Von-Neumann-Flaschenhals."}
        ${this.fails > 0 ? "<br>Für den zweiten Stern: beim ersten Prüfen richtig." : ""}
        ${this.fails === 0 && this.hintsShown > 0 ? "<br>Den dritten Stern gibt es, wenn Sie es ohne Hinweis schaffen." : ""}
        ${onward}
      </div>`;
    } else if (r) {
      feedback = `<div class="feedback error"><strong>Noch nicht ganz.</strong>
        <ul>${r.problems.map(p => `<li>${esc(p.message)}</li>`).join("")}</ul></div>`;
    }
    const hints = t.hinweise.slice(0, this.hintsShown).map(h => `<li>${esc(h)}</li>`).join("");

    this.side.innerHTML = `
      ${feedback}
      <div class="ziel"><strong>${t.architektur === "harvard" ? "Harvard-Architektur" : "Von-Neumann-Architektur"}</strong>
        <ul class="legende">
          <li><span class="swatch adresse"></span>Adressbus: welche Speicherzelle?</li>
          <li><span class="swatch daten"></span>Datenbus: die Daten selbst</li>
          <li><span class="swatch steuer"></span>Steuerbus: lesen/schreiben, wer ist gemeint?</li>
        </ul></div>
      <p class="tip">Ziehen Sie von einem Anschluss zum anderen -- oder tippen Sie beide nacheinander an.
        Ein weiterer Anschluss an einem Bus wird zur Abzweigung. Bus antippen: Breite einstellen oder löschen.</p>
      ${hints ? `<div class="hints"><strong>Hinweise</strong><ul>${hints}</ul></div>` : ""}`;
  }
}
